import * as React from 'react';
import { observer } from 'mobx-react';
import { action } from 'mobx';
import { Label, Button } from 'office-ui-fabric-react';
import ReactTable, { CellInfo, Column, FinalState, RowInfo } from 'react-table';
import { Tooltip } from 'reactstrap';

import { Permissions } from '@app/AppConstants';
import { ContractType, PersonContractViewModel, PersonFunctionViewModel, PlanningFunctionOverruleModel } from '@app/Models';

import InlineDateValidationService from '@app/Services/InlineDateValidationService';
import DateTimeService from '@app/Services/DateTimeService';
import { securityService } from '@app/Services/SecurityService';

import { Loading } from '@app/Components/Loading';
import { PlanningFunctionOverrulesDialog } from './Dialogs/PlaningFunctionOverrulesDialog';
import { InlineContractDropdownCell, InlineDateCell, TextCell, InlinePercentageCell, InlineCheckBoxCell, InlineTextboxCell } from '@app/Components/CellFormatters';
import { modalService } from '@app/Components/Modal';
import { AzureAuth } from '@app/Components/AzureAuth';
import { ContractEmploymentModelsDialog } from './Dialogs/ContractEmploymentModelsDialog';
import { EntryExitBlockStore } from './EntryExitBlockStore';

export type EntryExitBlockProps = {
    personId: string;
    personDepartment?: string;
};

@observer
export class EntryExitBlock extends React.Component<EntryExitBlockProps, {}> {
    private readonly _store: EntryExitBlockStore;
    private _planningGroupInputRef: React.RefObject<InlineTextboxCell<PersonFunctionViewModel>> = React.createRef();

    constructor(props: EntryExitBlockProps) {
        super(props);
        this._store = new EntryExitBlockStore(this.props.personId);
    }

    componentDidUpdate(prevProps: Readonly<EntryExitBlockProps>) {
        if (prevProps.personId !== this.props.personId) {
            this._store.personId = this.props.personId;
            void this._store.loadPersonFunction(this.props.personId);
            void this._store.loadPersonContract(this.props.personId);
        }
    }

    render() {
        const { personId, personContracts, personFunctions } = this._store;
        if (!personId) return;

        const { hasPermission, permissionCss } = securityService;

        return (
            <fieldset className="person-info-entry">
                <legend>Employment/Function</legend>
                <div className="row">
                    <div className="col-xl-12 col-md-12 col-sm-12 person-info-block" id={this._store.prefix}>
                        <Loading loading={this._store.personContractLoader.isPending} />
                        <Tooltip
                            placement="bottom"
                            isOpen={!!this._store.renderReason()}
                            boundariesElement={this._store.activeRowId}
                            target={this._store.activeRowId}
                            delay={{ show: 150, hide: 0 }}
                            container=".person-info-entry">
                            {this._store.renderReason()}
                        </Tooltip>
                        <div className="table-label">
                            <Label>Employment</Label>
                        </div>
                        {personContracts &&
                        <ReactTable<PersonContractViewModel>
                            data={this._store.groupedPersonContracts}
                            columns={this._contractColumns}
                            getTrProps={(props: FinalState, info?: RowInfo) => {
                                return {
                                    id: info ? this._store.prefix + info.row.id : '',
                                    onMouseOver: () => {
                                        this._store.activeRow = info && info.row._original as PersonContractViewModel || null;
                                    },
                                    onMouseOut: () => { this._store.activeRow = null; }
                                };
                            }}
                            pageSize={personContracts.length > 4 ? personContracts.length : 4}
                            showPageSizeOptions={false}
                            showPagination={false}
                            className={'ReactTable-fixed-height'}
                        />
                        }
                    </div>
                    <div className="col-xl-12 col-md-12 col-sm-12 person-info-block">
                        <Loading loading={this._store.personFunctionLoader.isPending} />
                        <div className="table-label">
                            <Label>Function</Label>
                        </div>
                        {personFunctions &&
                        <ReactTable<PersonFunctionViewModel>
                            data={personFunctions}
                            columns={this._functionColumns}
                            pageSize={personFunctions.length > 4 ? personFunctions.length : 4}
                            showPageSizeOptions={false}
                            getTrProps={this._setRowColor}
                            showPagination={false}
                            className={'ReactTable-fixed-height'}
                        />
                        }
                    </div>
                </div>
            </fieldset>
        );
    }

    private _renderContractFromDate = (cellInfo: CellInfo) => {
        return <InlineDateCell<PersonContractViewModel>
            value={cellInfo.value}
            row={cellInfo.row._original}
            fieldKey={cellInfo.column.id as keyof PersonContractViewModel}
            disabled={cellInfo.row._original.isFinalized}
            isReadOnly
            onValidate={InlineDateValidationService.validateDate}
            onSelect={this._onContractTableColumnChange} />;
    };

    private _renderContractToDate = (cellInfo: CellInfo) => {
        const modelsByContract = this._store.personContracts.filter(c => c.externalId === cellInfo.row._original.externalId);
        let maxDateByContract: Date | null = cellInfo.value;
        for (let contract of modelsByContract) {
            if (!cellInfo.value) break;
            if (!contract.dateTo) {
                maxDateByContract = null;
            }
            if (contract.dateTo && cellInfo.value && contract.dateTo.getTime() > cellInfo.value.getTime()) {
                maxDateByContract = contract.dateTo;
            }
        }

        return <InlineDateCell<PersonContractViewModel> 
            value={maxDateByContract}
            row={cellInfo.row._original}
            fieldKey={cellInfo.column.id as keyof PersonContractViewModel}
            isReadOnly
            canBeEmpty
            onValidate={InlineDateValidationService.validateDate}
            onSelect={this._onContractTableColumnChange}
            onConfirmation={this._onConfirmContractDateTo} />;
    };

    @action.bound
    private _onConfirmContractDateTo(row: PersonContractViewModel){
        return modalService.showConfirmation(`Do you really want to set contract end to '${(row.dateTo ? DateTimeService.toUiDate(row.dateTo) : '<empty>')}'?`);
    }

    private _renderFunctionDateFrom = (cellInfo: CellInfo) => {
        return <TextCell value={DateTimeService.toUiDate(cellInfo.value)} />;
    };

    private _renderFunctionDateTo = (cellInfo: CellInfo) => {
        const row = cellInfo.row as PersonFunctionViewModel;
        return <InlineDateCell<PersonFunctionViewModel>
            value={cellInfo.value}
            row={cellInfo.row._original}
            fieldKey={cellInfo.column.id as keyof PersonFunctionViewModel}
            isReadOnly
            canBeEmpty
            onValidate={InlineDateValidationService.validateDate}
            onSelect={this._onFunctionTableColumnChange}
            onConfirmation={this._onConfirmFunctionDateTo} />;
    };

    private _renderPlanningGroup = (cellInfo: CellInfo) => {
        const row = cellInfo.row as PersonFunctionViewModel;
        return <InlineTextboxCell<PersonFunctionViewModel>
            ref={this._planningGroupInputRef}
            cellInfo={cellInfo}
            value={cellInfo.value}
            row={cellInfo.row._original}
            fieldKey="planningGroup"
            disabled={!securityService.hasPermission(Permissions.EditFunctionPlanningGroup, !AzureAuth.isReadOnly)}
            onChange={async (row) => {
                if (await this._onConfirmPlanningGroup(row)){
                    this._onFunctionTableColumnChange(row);
                } else {
                    this._planningGroupInputRef.current?.clearValue();
                }
            }}
        />;
    };

    @action.bound
    private _onConfirmFunctionDateTo(row: PersonFunctionViewModel){
        return modalService.showConfirmation(`Do you really want to set function end to '${(row.dateTo ? DateTimeService.toUiDate(row.dateTo) : '<empty>')}'?`);
    }
    
    @action.bound
    private _onConfirmPlanningGroup(row: PersonFunctionViewModel){
        return modalService.showConfirmation(`Do you really want to set planning group '${(row.planningGroup || '<empty>')}'?`);
    }
    
    private _renderFunctionDateSince = (cellInfo: CellInfo) => {
        return <TextCell value={DateTimeService.toUiDate(cellInfo.value)} />;
    };

    private _renderFunctionOverrules = (cellInfo: CellInfo) => {
        if (cellInfo.row._original.planningFunctionOverrules && cellInfo.row._original.planningFunctionOverrules.length > 0) {
            return cellInfo.row._original.planningFunctionOverrules.map((el: PlanningFunctionOverruleModel) => {
                return <div key={el.id}>{el.functionOverruleType + ' ' + DateTimeService.toUiDate(el.dateFrom) + ' - ' + (el.dateTo ? DateTimeService.toUiDate(el.dateTo) : '')}</div>;
            });
        } else {
            return <span />;
        }
    };

    private _renderContractEmploymentType = (cellInfo: CellInfo) => {
        return <InlineContractDropdownCell 
            value={cellInfo.value} 
            onChangeHandler={this._onContractTableColumnChange}
            row={cellInfo.row._original} 
            fieldKey={cellInfo.column.id || ''} 
            disabled={cellInfo.row._original.isFinalized}
            isReadOnly
            personFunctions={this._store.personFunctions}
        />;
    };

    private _renderFunctionEmploymentType = (cellInfo: CellInfo) => {
        return <TextCell value={cellInfo.value} />;
    };

    private _renderContractInlineTextCell = (cellInfo: CellInfo) => {
        return <InlinePercentageCell 
            value={cellInfo.value} 
            onChange={this._onContractTableColumnChange} 
            row={cellInfo.row._original} 
            fieldKey="officePercentage"
            isReadOnly
        />;
    };

    private _renderContractPartTimeTextCell = (cellInfo: CellInfo) => {
        let value = cellInfo.value;
        const contract = cellInfo.row._original as PersonContractViewModel;
        if (contract.type === ContractType.Fix && value === 1) {
            return <TextCell value={(Math.round(Number(cellInfo.value) * 100000) / 1000).toString() + '%'} />;
        } else {
            return <InlinePercentageCell 
                value={value} 
                onChange={this._onContractTableColumnChange} 
                row={cellInfo.row._original} 
                fieldKey="partTimePercentage"
                isReadOnly
            />;
        }
    };

    private _renderFlyingDutyPercentCell = (cellInfo: CellInfo) => {
        const value = cellInfo.value ? (Math.round(Number(cellInfo.value) * 100000) / 1000).toString() : '0';
        return <TextCell value={value + '%'} />;
    };

    private _renderFinalizeContractCell = (cellInfo: CellInfo) => {
        if (cellInfo.value === true) {
            return <i className="ms-Icon ms-Icon-action ms-Icon--Accept" aria-hidden="true" />;
        }
	    if (securityService.hasPermission(Permissions.EditContractIsFinalized, !AzureAuth.isReadOnly)) {
            const finalizeContractCallBack = async () => {
                await modalService.showConfirmation('Are you sure you want to finalize this contract?', 'Warning', {
                    onConfirm: async () => {
                        cellInfo.row._original.isFinalized = true;
                        // cellInfo.row.isFinalized = true;
                        await this._onContractTableColumnChange(cellInfo.row._original);
                    }
                });
            };
            return <Button onClick={finalizeContractCallBack}>Finalize</Button>;
        } else {
            return <i className="ms-Icon ms-Icon-action ms-Icon--Cancel" aria-hidden="true" />;
        }
    };
    
    private _renderExternalEmployeeContractCell = (cellInfo: CellInfo) => {
        return <InlineCheckBoxCell 
            value={cellInfo.value}
            onChange={this._onContractTableColumnChange}
            row={cellInfo.row._original} 
            fieldKey={cellInfo.column.id || ''}
            isReadOnly
        />;
    };

    private _renderContractEmploymentModelsColumn = (cellInfo: CellInfo) => {
        return <i className="ms-Icon ms-Icon-action ms-Icon--RedEye" aria-hidden="true" onClick={() => this._onContractEmploymentModelsClick(cellInfo)} />;
    };

    @action.bound
    private _onContractEmploymentModelsClick(cellInfo: CellInfo) {
        modalService.show(ContractEmploymentModelsDialog, {
            store: this._store,
            selectedContractId: cellInfo.row.id,
            updateContract: this._store.updateContract,
            refreshData: this._onContractEmploymentChangeHandler
        });
    }

    @action.bound
    private _onContractEmploymentChangeHandler() {
        void this._store.loadPersonContract(this.props.personId);
    }

    private _renderPlanningFunctionOverrulesColumn = (cellInfo: CellInfo) => {
        return <i className="ms-Icon ms-Icon-action ms-Icon--RedEye" aria-hidden="true" onClick={() => this._onPlanningFunctionOverrulesClick(cellInfo)} />;
    };

    @action.bound
    private _onPlanningFunctionOverrulesClick(cellInfo: CellInfo){
        modalService.show(PlanningFunctionOverrulesDialog, {
            personFunction: cellInfo.row._original,
            onPersonFunctionOverruleUpdateHandler: this._onFunctionOverruleChangeHandler
        });
    }

    @action.bound
    private _onFunctionOverruleChangeHandler() {
        void this._store.loadPersonFunction(this.props.personId);
    }

    private _renderCheckBoxColumn = (cellInfo: CellInfo) => {
        if (cellInfo.value > 0) {
            return <i className="ms-Icon ms-Icon-action ms-Icon--Accept" aria-hidden="true" />;
        }
        return null;
    };

    private _onContractTableColumnChange = (params: PersonContractViewModel) => {
        return this._store.updateContract(params, false, true);
    };

    private _onFunctionTableColumnChange = (params: PersonFunctionViewModel) => {
        void this._store.updateFunction(params);
    };

    private _setRowColor = (state: unknown, rowInfo: RowInfo | undefined) => {
        let conf = {};
        if (rowInfo && rowInfo.row && rowInfo.row.planningFunctionOverruleCount > 0) {
            conf = { style: { backgroundColor: 'rgba(255,165,0, 0.5)' } };
        }
        return conf;
    };

    private _functionColumns: Column<PersonFunctionViewModel>[] = [
        {
            Header: 'From',
            accessor: 'dateFrom',
            Cell: this._renderFunctionDateFrom,
            className: securityService.permissionCss(Permissions.ViewFunctionDateFrom, Permissions.EditFunctionDateFrom)
        },
        {
            Header: 'To',
            accessor: 'dateTo',
            Cell: this._renderFunctionDateTo,
            className: securityService.permissionCss(Permissions.ViewFunctionDateTo, Permissions.EditFunctionDateTo)
        },
        {
            Header: 'Function',
            accessor: 'functionName',
            Cell: this._renderFunctionEmploymentType,
            className: securityService.permissionCss(Permissions.ViewFunctionFunctionType, Permissions.EditFunctionType)
        },
        {
            Header: 'Since',
            accessor: 'dateSince',
            Cell: this._renderFunctionDateSince,
            className: securityService.permissionCss(Permissions.ViewFunctionDateSince, Permissions.EditFunctionDateSince)
        },
        {
            Header: 'Planning Group',
            accessor: 'planningGroup',
            Cell: this._renderPlanningGroup,
            className: securityService.permissionCss(Permissions.ViewFunctionPlanningGroup, Permissions.EditFunctionPlanningGroup)
        },
        {
            Header: 'Function Overrules',
            accessor: 'planningFunctionOverrules',
            Cell: this._renderFunctionOverrules,
            show: securityService.hasPermission(Permissions.ViewObjectPersonFunctionOverrule),
            className: securityService.permissionCss(Permissions.ViewObjectPersonFunctionOverrule)
        },
        {
            Header: 'Has Overrides',
            accessor: 'planningFunctionOverruleCount',
            Cell: this._renderCheckBoxColumn,
            maxWidth: 100,
            show: securityService.hasPermission(Permissions.ViewObjectPersonFunctionOverrule),
            className: securityService.permissionCss(Permissions.ViewObjectPersonFunctionOverrule)
        },
        {
            Header: '',
            accessor: 'id',
            Cell: this._renderPlanningFunctionOverrulesColumn,
            maxWidth: 50,
            show: securityService.hasPermission(Permissions.ViewObjectPersonFunctionOverrule),
            className: securityService.permissionCss(Permissions.ViewObjectPersonFunctionOverrule, Permissions.EditObjectPersonFunctionOverrule)
        }
    ];

    private _contractColumns: Column<PersonContractViewModel>[] = [
        {
            Header: 'From',
            accessor: 'dateFrom',
            Cell: this._renderContractFromDate,
            className: securityService.permissionCss(Permissions.ViewContractDateFrom, Permissions.EditContractDateFrom),
        },
        {
            Header: 'To',
            accessor: 'dateTo',
            Cell: this._renderContractToDate,
            className: securityService.permissionCss(Permissions.ViewContractDateTo, Permissions.EditContractDateTo),
        },
        {
            Header: 'Type',
            accessor: 'type',
            Cell: this._renderContractEmploymentType,
            className: securityService.permissionCss(Permissions.ViewContractType, Permissions.EditContractType),
        },
        {
            Header: 'Employment',
            accessor: 'partTimePercentage',
            Cell: this._renderContractPartTimeTextCell,
            className: securityService.permissionCss(Permissions.ViewContractPartTimePercentage, Permissions.EditContractPartTimePercentage),
        },
        {
            Header: 'Office',
            accessor: 'officePercentage',
            Cell: this._renderContractInlineTextCell,
            className: securityService.permissionCss(Permissions.ViewContractOfficePercentage, Permissions.EditContractOfficePercentage),
        },
        {
            Header: 'Flying Duty',
            accessor: 'flyingDutyPercentage',
            Cell: this._renderFlyingDutyPercentCell,
            show: securityService.hasPermission(Permissions.ViewContractFlyingDutyPercentage),
            className: 'text-center' + securityService.permissionCss(Permissions.ViewContractFlyingDutyPercentage),
        },
        {
            Header: 'Is External',
            accessor: 'isExternalEmployee',
            Cell: this._renderExternalEmployeeContractCell,
            show: securityService.hasPermission(Permissions.ViewContractIsExternalEmployee),
            className: 'text-center' + securityService.permissionCss(Permissions.ViewContractIsExternalEmployee, Permissions.EditContractIsExternalEmployee),
        },
        {
            Header: 'Finalized',
            accessor: 'isFinalized',
            Cell: this._renderFinalizeContractCell,
            show: securityService.hasPermission(Permissions.ViewContractIsFinalized),
            className: 'text-center' + securityService.permissionCss(Permissions.ViewContractIsFinalized, Permissions.EditContractIsFinalized),
        },
        {
            Header: '',
            accessor: 'id',
            Cell: this._renderContractEmploymentModelsColumn,
            maxWidth: 50,
            show: securityService.hasPermission(Permissions.ViewObjectPersonContractMainPart),
            className: securityService.permissionCss(Permissions.ViewObjectPersonContractMainPart, Permissions.EditObjectPersonContractMainPart)
        }
    ];
}
